import { cn } from '@/lib/utils';

interface SkeletonProps {
  /** Additional CSS classes */
  className?: string;
}

/**
 * Base skeleton component with pulse animation.
 */
export function Skeleton({ className }: SkeletonProps) {
  return <div className={cn('animate-pulse rounded-md bg-muted', className)} aria-hidden="true" />;
}

interface SkeletonRowProps {
  /** Additional CSS classes */
  className?: string;
}

/**
 * A single row skeleton, useful for text lines.
 */
export function SkeletonRow({ className }: SkeletonRowProps) {
  return <Skeleton className={cn('h-4 w-full', className)} />;
}

interface SkeletonCardProps {
  /** Additional CSS classes */
  className?: string;
}

/**
 * A card-shaped skeleton with header and content areas.
 */
export function SkeletonCard({ className }: SkeletonCardProps) {
  return (
    <div className={cn('rounded-lg border bg-card p-4 space-y-3', className)} aria-hidden="true">
      <Skeleton className="h-5 w-2/3" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-4/5" />
    </div>
  );
}

interface SkeletonListProps {
  /** Number of rows to display */
  count?: number;
  /** Additional CSS classes for the container */
  className?: string;
  /** Gap between rows (Tailwind gap class) */
  gap?: 'gap-1' | 'gap-2' | 'gap-3' | 'gap-4';
}

/**
 * A list of skeleton rows for loading states.
 */
export function SkeletonList({ count = 3, className, gap = 'gap-3' }: SkeletonListProps) {
  return (
    <div className={cn('flex flex-col', gap, className)} role="status" aria-label="Loading">
      {Array.from({ length: count }).map((_, index) => (
        <SkeletonRow key={index} />
      ))}
    </div>
  );
}

interface SkeletonAvatarProps {
  /** Size of the avatar */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
}

const AVATAR_SIZES = {
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
} as const;

/**
 * A circular avatar skeleton.
 */
export function SkeletonAvatar({ size = 'md', className }: SkeletonAvatarProps) {
  return <Skeleton className={cn('rounded-full', AVATAR_SIZES[size], className)} />;
}

interface SkeletonTextBlockProps {
  /** Number of lines */
  lines?: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * A block of skeleton text lines with varying widths.
 */
export function SkeletonTextBlock({ lines = 3, className }: SkeletonTextBlockProps) {
  const widths = ['w-full', 'w-4/5', 'w-3/4', 'w-5/6', 'w-2/3'];

  return (
    <div className={cn('space-y-2', className)} aria-hidden="true">
      {Array.from({ length: lines }).map((_, index) => (
        <SkeletonRow key={index} className={widths[index % widths.length]} />
      ))}
    </div>
  );
}
